import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { reportBug } from '../../api/dev';
import { useSnackbar } from '../../context/SnackbarContext';

interface Props {
  open: boolean;
  wpId: number;
  wpNumber: string;
  wpTitle: string;
  onClose: () => void;
  onReported: (bugCount: number) => void;
}

export function BugReportDialog({ open, wpId, wpNumber, wpTitle, onClose, onReported }: Props) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { bug_count } = await reportBug(wpId, note.trim() || undefined);
      onReported(bug_count);
      showSnackbar(`Bug reported for ${wpNumber}`, 'success');
      setNote('');
      onClose();
    } catch {
      showSnackbar('Failed to report bug', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon color="error" />
        Report Bug — {wpNumber} {wpTitle}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optional note:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 200 }}
          helperText={`${note.length} / 200 characters`}
          placeholder="Short description of the bug…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={<BugReportIcon />}
        >
          Report Bug
        </Button>
      </DialogActions>
    </Dialog>
  );
}
